/**
 * Wireframe edge-line overlay for model geometries.
 *
 * When wireframeOpacity > 0 this renders EdgesGeometry line segments
 * on top of (or instead of) the solid model. The solid model fades
 * out proportionally as wireframe fades in, handled by ModelRenderer.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import type { ColoredGeometry } from '@/components/viewer/types';

function EdgeLines({
  geometry,
  color = 'white',
  opacity = 0.8,
}: {
  geometry: THREE.BufferGeometry;
  color?: string;
  opacity?: number;
}) {
  const edgesGeometry = useMemo(() => {
    return new THREE.EdgesGeometry(geometry, 15);
  }, [geometry]);

  const lineMaterial = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
    });
  }, [color, opacity]);

  const lineSegments = useMemo(() => {
    return new THREE.LineSegments(edgesGeometry, lineMaterial);
  }, [edgesGeometry, lineMaterial]);

  return <primitive object={lineSegments} />;
}

export function WireframeOverlay({
  geometry,
  coloredGeometries,
  wireframeOpacity,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  wireframeOpacity: number;
}) {
  if (wireframeOpacity <= 0) return null;

  return (
    <>
      {coloredGeometries.length > 0 ? (
        <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          {coloredGeometries.map((cg) => (
            <EdgeLines
              key={cg.geometry.uuid}
              geometry={cg.geometry}
              opacity={wireframeOpacity}
            />
          ))}
        </group>
      ) : geometry ? (
        <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <EdgeLines geometry={geometry} opacity={wireframeOpacity} />
        </group>
      ) : null}
    </>
  );
}
